import i18n from 'i18next';
import type { ConversationSummary } from '@/types/chat';

/**
 * The conversation toolbar's one real axis (2026-09-21).
 *
 * `sessionType` has been written at every creation site since #1731 phase 6,
 * and `hasVoice` marks a thread where any turn came from the call path.
 * Threads older than that carry neither field and read as text, which is
 * what they were — so the fallback is not a guess.
 *
 * Text vs voice is offered because the data already answers it. A toolbar
 * does not invent tools (ui-ux-guideline §2.3, C3): a "last 30 days" scope
 * would have been a control with nothing behind it on a list that is already
 * newest-first.
 */
export type ConversationScope = 'all' | 'voice' | 'text';

export function isVoiceThread(c: ConversationSummary): boolean {
  return c.sessionType === 'voice' || c.hasVoice === true;
}

/**
 * The title as the reader SEES it — the fallback is searchable too. #2109 R3
 * L5b: the fallback is in the reader's language, resolved when the row
 * renders (and when the search runs), so a Spanish reader finds an untitled
 * thread by the Spanish word they see.
 */
export function conversationTitle(c: ConversationSummary): string {
  return c.title || i18n.t('chat:archive.untitled');
}

export function conversationMatches(
  c: ConversationSummary,
  scope: ConversationScope,
  query: string,
): boolean {
  if (scope === 'voice' && !isVoiceThread(c)) return false;
  if (scope === 'text' && isVoiceThread(c)) return false;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return conversationTitle(c).toLowerCase().includes(q);
}
